import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpEventType } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { tap, takeUntil, Subject, from, concatMap, catchError, of, filter, Observable, map } from 'rxjs';
import { uniqueId } from 'lodash-es';

import { Customer, CustomerApi, MediaFile, Tag } from '@api/index';
import { MediaApi } from '@api/services/media-api';
import { TagsApi } from '@api/services/tags-api.service';
import { TranslateConfigService } from '@core/translate-config.service';
import { SystemMessageService } from '@core/services/system-message.service';
import { unsubscribeMixin } from '@core/unsubscribe';
import { Media } from '@core/services/media.service';
import { SelectedFile, MediaFormat, UploadStatus } from '@app/models/media-files';
import { CustomSnackbarComponent } from '@shared/custom-snackbar/custom-snackbar.component';
import { ClientApi } from '@api/services/client-api';
import { IdName } from '@api/models/idname';
import { FILE_TYPES } from '@app/constants';
import { base64ToBlob } from '@shared/utils/utils';
import { FILE_SIZE } from '@shared/constants/file-size';
import { CurrencyEnum } from '@app/shared/models/enums/currency.enum';
import { CURRENCIES } from '@app/shared/constants/currency.constants';
import { PRICING_MODEL } from '@app/shared/constants/pricing-model.constants';
import { PricingDetails } from '@api/models/pricing';
import { PricingModelEnum } from '@app/shared/models/enums/pricingModel.enum';

interface ModalData {
  media?: MediaFile;
  customerIdModal?: number;
  isDefault?: boolean;
}

@Component({
  selector: 'flow-media-create-modal',
  templateUrl: './media-create.modal.html',
  styleUrls: ['./media-create.modal.scss']
})
export class MediaCreateModal extends unsubscribeMixin() implements OnInit, OnDestroy {
  cancelUploads$ = new Subject<void>();
  form: FormGroup;
  mediasArray: any[] = [];
  uploadedFiles: Media[] = [];
  selectedFile: SelectedFile | null = null;
  clients: IdName[] = [];
  customers: Customer[] = [];
  tags: Tag[];
  disableUpload: boolean = false;
  isEdit: boolean = false;
  reset: boolean = false;
  uploadingDone: boolean = false;
  supportedFileTypes = [...FILE_TYPES.image, ...FILE_TYPES.video];
  currencies = CURRENCIES;
  pricingModels = PRICING_MODEL;
  pricing: PricingDetails = { currency: null, pricingModel: null };

  constructor(
    public dialogRef: MatDialogRef<MediaCreateModal>,
    private mediaApi: MediaApi,
    private formBuilder: FormBuilder,
    private translateConfigService: TranslateConfigService,
    private systemMessageService: SystemMessageService,
    private dialog: MatDialog,
    private clientApi: ClientApi,
    private customerApi: CustomerApi,
    private tagsApi: TagsApi,
    @Inject(MAT_DIALOG_DATA) public data: ModalData
  ) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.getClients();
    this.getMediaPriceDetails();
    this.getTags();
  }

  getTags(): void {
    this.tagsApi.getTags({ customerIds: [this.data.customerIdModal] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((tags) => {
        this.tags = tags || [];
      });
  }

  initForm() {
    this.form = this.formBuilder.group({
      customerId: [this.data.customerIdModal, Validators.required],
      name: ['', Validators.required],
      clientId: [''],
      tags: [],
      isClientBoxChecked: [false],
      price: [0],
      currency: [CurrencyEnum.SEK],
      pricingModel: [PricingModelEnum.FP],
      description: [null, Validators.maxLength(50)],
      intendedTargetGroups: [null, Validators.maxLength(50)],
      author: [null, Validators.maxLength(50)],
      brand: [null, Validators.maxLength(50)],
      skuArticleNumbers: [null, Validators.maxLength(50)],
    });
  }

  getClients() {
    this.clientApi.getClientOptions([this.data.customerIdModal])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((clients) => {
        this.clients = clients;
      });
  }

  getMediaPriceDetails() {
    this.customerApi.getMediaPriceDetails([this.data.customerIdModal])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((pricing) => {
        if (pricing) {
          this.pricing = pricing;
          this.updatePricingFormProps(pricing);
        }
      });
  }

  getDataByClient(clientId: number[]) {
    this.clientApi.getClientMediaPriceDetails(clientId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((pricing) => {
        this.pricing = pricing;
        this.updatePricingFormProps(pricing);
      });
  }

  updatePricingFormProps(pricing: PricingDetails): void {
    this.form.get('currency')?.patchValue(pricing.currency);
    this.form.get('pricingModel')?.patchValue(pricing.pricingModel);
  }

  triggerFileInput() {
    const fileInput = document.getElementById('fileInput') as HTMLElement;
    fileInput.click();
  }

  displayTag(tag: Tag): string {
    return tag?.name ?? '';
  }

  selectFile(event: any): void {
    this.reset = false;
    this.selectedFile = event;
  }

  addFile() {
    if (this.selectedFile && this.form.get('name').valid) {
      const id = uniqueId();
      const formValues = this.form.getRawValue();
      const formBody = {
        id: id,
        name: formValues?.name,
        clientId: formValues.clientId,
        customerId: formValues.customerId,
        tags: formValues.tags,
        isClientBoxChecked: formValues?.isClientBoxChecked,
        index: id,
        price: formValues.price,
        currency: formValues.currency,
        pricingModel: formValues.pricingModel,
        description: formValues.description,
        intendedTargetGroups: formValues.intendedTargetGroups,
        author: formValues.author,
        brand: formValues.brand,
        skuArticleNumbers: formValues.skuArticleNumbers,
      };

      this.validateIfMediaNameIsUnique(formBody.name, formBody.customerId).subscribe({
        next: (isValid) => {
          if(!isValid){
            const updatedFile = { ...this.selectedFile, ...formBody };
            this.mediasArray.push(updatedFile);

            this.form.reset({
              customerId: this.data.customerIdModal,
              name: '',
              clientId: '',
              tags: [],
              isClientBoxChecked: false,
              price: 0,
              currency: this.pricing.currency ?? CurrencyEnum.SEK,
              pricingModel: this.pricing.pricingModel ?? PricingModelEnum.FP,
              description: null,
              intendedTargetGroups: null,
              author: null,
              brand: null,
              skuArticleNumbers: null,
            });

            this.selectedFile = null;
            this.reset = true;
          }
          else{
            this.systemMessageService.error(
              this.translateConfigService.instant('notifications.error.uniqueNameRequired')
            );
          }
        }
      });
    }
  }

  validateIfMediaNameIsUnique(name: string, customerId: number): Observable<boolean> {
    return this.mediaApi.validateUniqueName({ name: name, customerId: customerId }).pipe(
      map((response: boolean) => {
        return response && this.mediasArray.some(media => media.name?.trim().toLowerCase() === name.trim().toLowerCase()
        );
      })
    );
  }

  editFile(file: any) {
    if (file) {
      this.isEdit = true;
      this.selectedFile = file;
      this.form.patchValue({
        customerId: this.data.customerIdModal,
        name: file.name,
        clientId: file.clientId,
        tags: file.tags,
        isClientBoxChecked: file.isClientBoxChecked,
        price: file.price,
        currency: file.currency,
        pricingModel: file.pricingModel,
        description: file.description,
        intendedTargetGroups: file.intendedTargetGroups,
        author: file.author,
        brand: file.brand,
        skuArticleNumbers: file.skuArticleNumbers,
      });
    }
  }

  saveFile() {
    if (this.selectedFile && this.selectedFile.index) {
      const index = this.mediasArray.findIndex(control => control.index === this.selectedFile.index);
      if (index !== -1) {
        this.validateIfMediaNameIsUnique(this.form.get('name').value, this.data.customerIdModal).subscribe({
          next: (isValid) => {
            if(!isValid){
              const updatedFile = {
                ...this.mediasArray[index],
                customerId: this.data.customerIdModal,
                name: this.form.get('name').value,
                clientId: this.form.get('clientId').value,
                tags: this.form.get('tags').value,
                isClientBoxChecked: this.form.get('isClientBoxChecked').value,
                price: this.form.get('price').value,
                currency: this.form.get('currency').value,
                pricingModel: this.form.get('pricingModel').value,
                description: this.form.get('description').value,
                intendedTargetGroups: this.form.get('intendedTargetGroups').value,
                author: this.form.get('author').value,
                brand: this.form.get('brand').value,
                skuArticleNumbers: this.form.get('skuArticleNumbers').value,
              };

              this.mediasArray[index] = updatedFile;

              this.form.reset({
                customerId: this.data.customerIdModal,
                name: '',
                clientId: '',
                tags: [],
                isClientBoxChecked: false,
                price: 0,
                currency: this.pricing.currency ?? CurrencyEnum.SEK,
                pricingModel: this.pricing.pricingModel ?? PricingModelEnum.FP,
                description: null,
                intendedTargetGroups: null,
                author: null,
                brand: null,
                skuArticleNumbers: null,
              });
          
              this.isEdit = false;
              this.selectedFile = null;
            }
            else{
              this.systemMessageService.error(
                this.translateConfigService.instant('notifications.error.uniqueNameRequired')
              );
            }
          }
        });  
      }
    }
  }

  removeFile(id: string): void {
    const existingIndex = this.mediasArray.findIndex((item) => item.index === id);

    if (existingIndex !== -1) {
      this.mediasArray.splice(existingIndex, 1);
    }
  }

  newTag(name: string): Tag {
    return { id: null, name };
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private checkStorage() {
    const isExceeded = this.checkIfStorageIsExceeded();

    if (isExceeded) {
      this.dialog.open(CustomSnackbarComponent, {
        width: '600px',
        data: {
          msg: {
            title: "notifications.error.storageExceededTitle",
            content: this.translateConfigService.instant("notifications.error.storageExceeded")
          }
        }
      });
      return;
    }
  }

  private checkIfStorageIsExceeded() {
    const customer = this.form.get('customerId').value;
    const mediaTotalSize = this.mediasArray.reduce((acc, curr) => acc + curr.selectedFile.size, 0);
    return (mediaTotalSize + customer.currentStorageSize) > this.gigabytesToBytes(customer.storageLimitSize);
  }

  private checkFilesSize() {
    for (let i = 0; i < this.mediasArray.length; i++) {
      const file = this.mediasArray[i];
      if (file.selectedFile.size > FILE_SIZE) {
        this.systemMessageService.error(this.translateConfigService.instant("notifications.error.fileTooLarge"));
        return;
      }
    }
  }

  onSave(): void {
    if (this.mediasArray.length === 0) return;
    this.disableUpload = true;

    this.checkStorage();
    this.checkFilesSize();
    this.uploadFiles();
  }

  uploadFiles() {
    this.uploadingDone = false;
    from(this.mediasArray).pipe(
      filter(media => media.status !== UploadStatus.success),
      concatMap(media => this.processFile(media).pipe(
        filter((event) => event.type === HttpEventType.Response),
        tap(() => this.updateStatus(media, UploadStatus.success)),
        catchError((error) => {
          this.updateStatus(media, UploadStatus.error, error.message);
          return of(null);
        }),
        takeUntil(this.cancelUploads$)
      )),
      takeUntil(this.cancelUploads$)
    ).subscribe({
      error: (error) => {
        this.systemMessageService.error(this.translateConfigService.instant(error));
      },
      complete: () => {
        this.disableUpload = false;
        this.uploadingDone = true;

        if (this.mediasArray.every(media => media.status === UploadStatus.success)) {
          this.onUploadComplete();
        }
      }
    })
  }

  updateStatus(media, status: UploadStatus, errorMessage?: string) {
    const existingFile = this.mediasArray.find(file => file.index === media.index);
    if (existingFile) {
      existingFile.status = status;
      existingFile.errorMessage = errorMessage;
    } else {
      this.mediasArray.push({ media, status, errorMessage });
    }
  }

  processFile(media) {
    const model = this.createRequestModel(media);
    const uploadRequest = media?.format === MediaFormat.Image ? 'mediaUploadImage' : 'mediaUploadVideo';
    media.uploadProgress = null;

    const formData = new FormData();
    Object.entries(model).forEach(([key, value]) => {
      formData.append(key, value);
    });

    return this.mediaApi[uploadRequest](formData).pipe(
      tap((event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          media.uploadProgress = Math.round((event.loaded / (event.total ?? 1)) * 100);
        }

        if (event.type === HttpEventType.Response && event?.body) {
          this.uploadedFiles.push(event?.body);
        }
      })
    );
  }


  private onUploadComplete(): void {
    const file = this.data.isDefault ? this.uploadedFiles[0] : this.uploadedFiles;
    this.systemMessageService.success(this.translateConfigService.instant("notifications.success.mediaCreate"));
    this.dialogRef.close(file);
    this.disableUpload = false;
  }

  createRequestModel(media): string {
    const tagsJson = JSON.stringify(media?.tags || []);
    let requestModel: any = {};

    if (media.customerId !== undefined && media.customerId !== null) {
      requestModel.customerId = media.customerId
    }
    if (media?.name && media?.name.length > 0) {
      requestModel.name = media?.name;
    }
    if (media?.clientId) {
      requestModel.clientId = media?.clientId;
    }
    if (media?.selectedFile?.videoDuration) {
       requestModel.duration =  media?.selectedFile?.videoDuration;
    }
    if (!this.data.media) {
      requestModel.shouldCreateFolder = media.isClientBoxChecked || false;
    }
    if (media?.selectedFile) {
      requestModel.file = media?.selectedFile;
    }

    if (media?.format === MediaFormat.Video) {
      const byteCharacters = base64ToBlob(media?.filePreview);

      requestModel.videoThumbnail = byteCharacters;
      requestModel.videoThumbnailIndex = 0;
    }

    requestModel.tags = tagsJson;
    requestModel.mediaType = media?.format;
    requestModel.price = media.price;
    requestModel.currency = media.currency;
    requestModel.pricingModel = media.pricingModel;
    requestModel.description = media?.description ?? '';
    requestModel.intendedTargetGroups = media?.intendedTargetGroups ?? '';
    requestModel.author = media?.author ?? '';
    requestModel.brand = media?.brand ?? '';
    requestModel.skuArticleNumbers = media?.skuArticleNumbers ?? '';
    return requestModel;
  }

  getVideoDuration(selectedFile: File, file: SelectedFile): void {
    const video = document.createElement('video');
    video.src = window.URL.createObjectURL(selectedFile);
    video.preload = 'metadata';
    video.onloadedmetadata = () => (file.videoDuration = +video.duration.toFixed(2));
  }

  gigabytesToBytes(gigabytes) {
    const bytesInGigabyte = 1024 * 1024 * 1024;
    const bytes = gigabytes * bytesInGigabyte;
    return bytes;
  }

  override ngOnDestroy(): void {
    this.cancelUploads$.next();
    this.cancelUploads$.complete();
  }
}
